import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { GlobalVariables } from '../../../../globalVar';
import customAxios from '../../../../utils/customAxios';
import { getToast } from '../../../../utils/toastConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import moment from 'moment';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';

const HEADERS = {
  headers: {
    Authorization: 'bearer ' + localStorage.getItem('token'),
  },
};

const ConfirmSend = ({ showModal, setShowModal, handleSend }) => {
  const handleConfirm = async (e) => {
    handleClose();
    await handleSend(e);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  return (
    <Dialog open={showModal} maxWidth='sm' fullWidth onClose={handleClose}>
      <DialogTitle>
        <Typography className='inventory-movmodal-title'>
          ¿Seguro que desea enviar la visita?
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label='close'
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 20,
          top: 20,
          width: 40,
          height: 40,
        }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
      <DialogContent dividers>
        <DialogContentText>
          Al realizar esta acción el servicio se enviará inmediatamente al
          dispositivo del técnico y ya no se podrá editar la información.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant='contained' onClick={handleConfirm}>
          Confirmar
        </Button>
        <Button onClick={handleClose}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
};

const SendVisitModal = ({ visitId, showModal, setShowModal, refreshData }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [visitData, setVisitData] = useState({
    id: visitId,
    technical_id: null,
    tech_name: '',
    visit_date: moment(),
  });
  const [technicals, setTechnicals] = useState([]);
  const [showConfirmSend, setShowConfirmSend] = useState(false);

  //#region Fetches

  const fetchVisitData = async () => {
    const endpoint = `${GlobalVariables.getURL()}getVisitById/${visitId}`;
    const response = await customAxios.get(endpoint, HEADERS);

    return response.data.data;
  };

  const fetchClientTechs = async (id) => {
    try {
      const endpoint = `${GlobalVariables.getURL()}technicals/clients/${id}`;
      const response = await customAxios.get(endpoint, HEADERS);

      if (response.status === 204) {
        setTechnicals([]);
        return;
      }

      setTechnicals(response.data.data);
    } catch (error) {
      console.log('🚀 ~ error:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    }
  };

  const fetchData = async () => {
    setIsFetching(true);
    try {
      const [visitData] = await Promise.all([fetchVisitData()]);

      setVisitData({
        ...visitData,
        tech_name: visitData.tech_name,
        technical_id: visitData.technical_id,
        visit_date: moment(visitData.visit_date),
      });
    } catch (error) {
      console.log(error);
      getToast({ status: error, title: error.response.data.message });
    } finally {
      setIsFetching(false);
    }
  };

  //#endregion

  //#region Handlers

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowConfirmSend(true);
  };

  const sendVisit = async (e) => {
    e.preventDefault();

    const endpoint = `${GlobalVariables.getURL()}sendVisit/${visitId}`;
    setIsLoading(true);

    try {
      const updateRes = await customAxios.patch(
        endpoint,
        {
          visit_date: visitData.visit_date.toISOString(),
          technical_id: visitData.technical_id,
        },
        HEADERS
      );

      if (updateRes.status === 200) {
        getToast({
          status: 'success',
          title: 'Servicio enviado correctamente',
        });

        handleClose();
      }
    } catch (error) {
      console.log(error);
      getToast({ status: 'error', title: error.response.data.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const endpoint = `${GlobalVariables.getURL()}updateVisit/${visitId}`;
    setIsLoading(true);

    try {
      const updateRes = await customAxios.patch(
        endpoint,
        {
          visit_date: visitData.visit_date,
          technical_id: visitData.technical_id,
        },
        HEADERS
      );

      if (updateRes.status === 200) {
        getToast({
          status: 'success',
          title: 'Servicio guardado correctamente',
        });

        handleClose();
      }
    } catch (error) {
      console.log(error);
      getToast({ status: 'error', title: error.response.data.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetFields = () => {
    setVisitData({
      technical_id: null,
      tech_name: '',
      visit_date: moment(),
    });
  };

  const handleClose = () => {
    setShowModal(false);
    handleResetFields();
    refreshData(); // Refresh failure types data
  };

  //#endregion

  useEffect(() => {
    if (!showModal) return;

    fetchData();
  }, [showModal]);

  useEffect(() => {
    if (visitData.client_id) {
      fetchClientTechs(visitData.client_id);
    } else {
      setTechnicals([]);
    }
  }, [visitData.client_id]);

  return (
    <>
      <Dialog open={showModal} maxWidth='md' fullWidth onClose={handleClose}>
        <DialogTitle>
          <Typography className='inventory-movmodal-title'>
            Enviar Servicio
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 20,
            top: 20,
            width: 40,
            height: 40,
          }}
        >
          <FontAwesomeIcon icon={faTimes} />
        </IconButton>
        <LocalizationProvider
          dateAdapter={AdapterMoment}
          adapterLocale={moment.locale()}
        >
          <DialogContent dividers>
            <form id='send-visit-form' onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                {/* Visit date field */}
                <Grid item xs={6}>
                  {isFetching ? (
                    <Skeleton variant='rounded' height={56} />
                  ) : (
                    <DateTimePicker
                      disabled={isLoading}
                      label='Fecha y hora de asignación del técnico'
                      value={visitData.visit_date}
                      onChange={(value) => {
                        setVisitData((prev) => ({
                          ...prev,
                          visit_date: value,
                        }));
                      }}
                      slotProps={{
                        textField: {
                          disabled: isLoading,
                          fullWidth: true,
                        },
                      }}
                    />
                  )}
                </Grid>
                <Grid item xs={6}>
                  {isFetching ? (
                    <Skeleton variant='rounded' height={56} />
                  ) : (
                    <Autocomplete
                      options={technicals}
                      value={
                        technicals.find(
                          (t) => t.id === visitData.technical_id
                        ) || null
                      }
                      getOptionLabel={(t) => t.full_name || ''}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      onChange={(_, newValue) => {
                        setVisitData((prev) => ({
                          ...prev,
                          technical_id: newValue ? newValue.id : null,
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          disabled={isLoading}
                          {...params}
                          label='Técnico'
                          placeholder='Técnico...'
                        />
                      )}
                    />
                  )}
                </Grid>
              </Grid>
            </form>
          </DialogContent>
        </LocalizationProvider>
        <DialogActions>
          <Button
            variant='contained'
            form='send-visit-form'
            type='submit'
            disabled={
              isLoading || (visitData && !visitData.technical_id ? true : false)
            }
          >
            {isLoading ? (
              <CircularProgress size='2rem' />
            ) : (
              <Typography>Enviar</Typography>
            )}
          </Button>
          <Button variant='outlined' disabled={isLoading} onClick={handleSave}>
            {isLoading ? (
              <CircularProgress size='2rem' />
            ) : (
              <Typography>Guardar</Typography>
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmSend
        showModal={showConfirmSend}
        setShowModal={setShowConfirmSend}
        handleSend={sendVisit}
      />
    </>
  );
};

export default SendVisitModal;
